import {
  ABILITIES, ABILITY_NAMES, SKILLS, SKILL_KEYS,
  type Ability, type ProficiencyLevel, type SkillKey,
} from '@shared/rules/constants'
import type { DerivedStats } from '@shared/rules/calc'
import { formatModifier } from '@shared/rules/calc'
import { NumberField, Panel, SectionHeading } from '@/components/ui'

/** Rolls a d20 plus the given bonus, labelled for the DM's feed. */
export type RollHandler = (formula: string, context: string) => void

const d20 = (bonus: number): string => `1d20${bonus < 0 ? bonus : `+${bonus}`}`
import { cn } from '@/lib/cn'

// ---------- Ability scores ----------

export function AbilityColumn({
  scores, editing, onChange, onRoll,
}: {
  scores: Record<Ability, number>
  derived: DerivedStats
  editing: boolean
  onChange: (ability: Ability, value: number) => void
  onRoll?: RollHandler
}) {
  return (
    <div className="grid grid-cols-3 gap-2.5 sm:grid-cols-6 lg:grid-cols-2">
      {ABILITIES.map((ability) => {
        const modifier = Math.floor((scores[ability] - 10) / 2)
        const rollable = !editing && onRoll
        return (
        <div key={ability} className="stat-slab gap-0.5">
          <span className="text-[10px] font-semibold uppercase tracking-wider text-ink-400">{ability}</span>
          {rollable ? (
            <button
              type="button"
              onClick={() => onRoll(d20(modifier), `${ABILITY_NAMES[ability]} check`)}
              title={`Roll a ${ABILITY_NAMES[ability]} check`}
              className="rounded-md px-2 font-mono text-2xl leading-tight tabular-nums text-gold-400 transition-colors hover:bg-gold-900/40"
            >
              {formatModifier(modifier)}
            </button>
          ) : (
            <span className="font-mono text-2xl leading-tight tabular-nums text-gold-400">
              {formatModifier(modifier)}
            </span>
          )}
          {editing ? (
            <NumberField
              min={1}
              max={30}
              fallback={10}
              value={scores[ability]}
              aria-label={ABILITY_NAMES[ability]}
              onChange={(next) => onChange(ability, next)}
              className="mt-1 w-14 rounded-md py-0.5 text-xs"
            />
          ) : (
            <span className="mt-0.5 font-mono text-xs tabular-nums text-ink-500">{scores[ability]}</span>
          )}
        </div>
        )
      })}
    </div>
  )
}

// ---------- Saving throws ----------

export function SavesPanel({
  derived, saveProficiencies, editing, onToggle, onRoll,
}: {
  derived: DerivedStats
  saveProficiencies: Ability[]
  editing: boolean
  onToggle: (ability: Ability) => void
  onRoll?: RollHandler
}) {
  return (
    <Panel className="p-4">
      <SectionHeading>Saving throws</SectionHeading>
      <ul className="space-y-0.5">
        {ABILITIES.map((ability) => {
          const proficient = saveProficiencies.includes(ability)
          return (
            <li key={ability}>
              <ProficiencyRow
                label={ABILITY_NAMES[ability]}
                value={derived.saves[ability]}
                marker={proficient ? 'proficient' : null}
                editing={editing}
                onClick={() => onToggle(ability)}
                onRoll={onRoll && (() =>
                  onRoll(d20(derived.saves[ability]), `${ABILITY_NAMES[ability]} save`))}
              />
            </li>
          )
        })}
      </ul>
    </Panel>
  )
}

// ---------- Skills ----------

const NEXT: Record<'none' | ProficiencyLevel, ProficiencyLevel | null> = {
  none: 'proficient',
  proficient: 'expertise',
  expertise: null,
}

export function SkillsPanel({
  derived, skillProficiencies, editing, onChange, onRoll,
}: {
  derived: DerivedStats
  skillProficiencies: Partial<Record<SkillKey, ProficiencyLevel>>
  editing: boolean
  onChange: (skill: SkillKey, level: ProficiencyLevel | null) => void
  onRoll?: RollHandler
}) {
  return (
    <Panel className="p-4">
      <SectionHeading>
        Skills{editing && <span className="ml-2 normal-case tracking-normal text-ink-500">— tap to cycle none → proficient → expertise</span>}
      </SectionHeading>
      <ul className="grid gap-x-6 sm:grid-cols-2">
        {SKILL_KEYS.map((skill) => (
          <li key={skill}>
            <ProficiencyRow
              label={SKILLS[skill].name}
              sublabel={SKILLS[skill].ability}
              value={derived.skills[skill]}
              marker={skillProficiencies[skill] ?? null}
              editing={editing}
              onClick={() => onChange(skill, NEXT[skillProficiencies[skill] ?? 'none'])}
              onRoll={onRoll && (() =>
                onRoll(d20(derived.skills[skill]), `${SKILLS[skill].name} check`))}
            />
          </li>
        ))}
      </ul>
    </Panel>
  )
}

// ---------- Shared row ----------

function ProficiencyRow({
  label, sublabel, value, marker, editing, onClick, onRoll,
}: {
  label: string
  sublabel?: string
  value: number
  marker: ProficiencyLevel | null
  editing: boolean
  onClick: () => void
  onRoll?: (() => void) | undefined
}) {
  const content = (
    <>
      <Pip level={marker} />
      <span className="flex-1 truncate text-left text-sm text-ink-200">{label}</span>
      {sublabel && <span className="text-[10px] uppercase tracking-wider text-ink-500">{sublabel}</span>}
      <span className="w-9 text-right font-mono text-sm tabular-nums text-ink-100">{formatModifier(value)}</span>
    </>
  )

  const className = 'flex w-full items-center gap-2.5 rounded-lg px-1.5 py-1.5'

  if (editing) {
    return (
      <button
        type="button"
        onClick={onClick}
        aria-pressed={marker !== null}
        className={cn(className, 'transition-colors hover:bg-ink-800')}
      >
        {content}
      </button>
    )
  }

  if (onRoll) {
    return (
      <button
        type="button"
        onClick={onRoll}
        title={`Roll ${label}`}
        className={cn(className, 'transition-colors hover:bg-gold-900/30')}
      >
        {content}
      </button>
    )
  }

  return <div className={className}>{content}</div>
}

function Pip({ level }: { level: ProficiencyLevel | null }) {
  return (
    <span
      aria-hidden
      title={level ?? 'not proficient'}
      className={cn(
        'size-2 shrink-0 rounded-full border transition-colors',
        level === 'expertise' ? 'border-gold-400 bg-gold-400 ring-2 ring-gold-400/30'
        : level === 'proficient' ? 'border-gold-500 bg-gold-500'
        : 'border-ink-600',
      )}
    />
  )
}

// ---------- Vitals ----------

export function VitalsRow({ derived, speed, hitDiceTotal, hitDiceSpent, hitDie }: {
  derived: DerivedStats
  /** Preformatted, so a flying or swimming speed can sit alongside walking. */
  speed: string
  hitDiceTotal: number
  hitDiceSpent: number
  hitDie: number | null
}) {
  const remaining = Math.max(0, hitDiceTotal - hitDiceSpent)
  return (
    <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3 lg:grid-cols-6">
      <Vital label="Armor class" value={derived.armorClass} />
      <Vital label="Initiative" value={formatModifier(derived.initiative)} />
      <Vital label="Speed" value={speed} />
      <Vital label="Proficiency" value={formatModifier(derived.proficiencyBonus)} />
      <Vital label="Passive Per." value={derived.passivePerception} />
      <Vital label="Hit dice" value={hitDie ? `${remaining}d${hitDie}` : '—'} />
    </div>
  )
}

function Vital({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="stat-slab gap-1">
      <span className="font-mono text-xl tabular-nums text-ink-100">{value}</span>
      <span className="text-[10px] uppercase tracking-wider text-ink-500">{label}</span>
    </div>
  )
}
